/** The coach's voice in the booth (Football 101): how a coach line meets the announcer's match calls. */
import type { AudioEngine } from '@hf/audio';
import { ui } from '../store';
import type { TutorialView } from '../types';

/** Booth priorities of the coach (the bridge's match calls are 1–2, its big calls 2). */
export const COACH_HEADLINE_PRIORITY = 4;
/**
 * A lesson's intro outranks the other headlines: the booth only cuts a STRICTLY lower line, so "Now stop HIM!" used
 * to queue behind a 3.4 s "Rookie camp is complete!" and expire unheard when NEXT came quickly (QA round 2).
 */
export const COACH_INTRO_PRIORITY = 5;
export const COACH_ASIDE_PRIORITY = 1;

/**
 * One coach line into the booth. A lesson's headline (intro, lesson / camp clear, graduation) cuts whatever is
 * playing and clears the stale queue behind it, and an intro wipes the captions of the lesson before ("ROOKIE CAMP IS
 * COMPLETE!" stayed on screen into Game Day) — only once the booth took the new line, so a caption never vanishes
 * from under a line still speaking. Praise and retry lines are asides: praise is dropped while the booth is already
 * calling the play (it used to cut "WHAT A FAKE" 20 ms in); a retry line waits for the booth and follows it.
 */
export const playCoach = (
  audio: Pick<AudioEngine, 'announce' | 'stats'>,
  v: NonNullable<TutorialView['voice']>,
): void => {
  const headline = v.kind === 'intro' || v.kind === 'clear';
  if (headline) {
    const priority = v.kind === 'intro' ? COACH_INTRO_PRIORITY : COACH_HEADLINE_PRIORITY;
    let said = audio.announce(v.line, { priority });
    if (said === null && v.alt) said = audio.announce(v.alt, { priority });
    if (said !== null && v.kind === 'intro') ui.captions.value = [];
    return;
  }
  if (audio.stats?.().speaking) {
    // the coach's retry encouragement FOLLOWS the booth's call of the miss ("Ohhh… no good!" → "Almost. Again!"):
    // dropped, the learner only heard the stadium judge him (QA round 3). Praise still yields to the booth.
    if (v.kind === 'retry') audio.announce(v.line, { priority: COACH_ASIDE_PRIORITY, wait: RETRY_WAIT_S });
    return;
  }
  audio.announce(v.line, { priority: COACH_ASIDE_PRIORITY });
};

/** How long the retry line may wait for the booth to finish its call (s). */
export const RETRY_WAIT_S = 3.5;
